import React, { createContext, Fragment, JSX, useContext, useEffect, useRef, useState } from "react"
import { DataSnapshot, getDatabase, onValue, ref, set } from "firebase/database";
import { AppContext, DataContext, useDatabase } from "../GlobalData"
import { produce } from "immer"
import { MurmurHash3 } from 'murmurhash-wasm';
export const DatabaseProvider = ({
    query,
    children
}: {
    query: string,
    children: JSX.Element | JSX.Element[]
}) => {
    const app = useContext(AppContext);
    if (app == null) {
        throw new Error("This element must be used within an AppContext")
    }
    const [data, setData] = useState(null as unknown);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState({
        "error": false
    } as {
        "error": false,
    } | {
        "error": true,
        "message": string
    })
    const database = getDatabase(app)
    const [retryCallback, setRetryCallback] = useState(() => () => { })
    useEffect(() => {
        let currentunsub: () => void = () => { }
        function setListener() {
            currentunsub = onValue(ref(database, query), (snapshot: DataSnapshot) => {
                setData(snapshot.toJSON());
                setLoading(false)
            }, (error) => {
                setError({
                    error: true,
                    message: error.message
                })
            })
        }
        setListener()
        setRetryCallback(() => {
            setLoading(true)
            setListener()
        })
        return () => currentunsub()
    }, [database, query])
    const modifyCallback: (modifier: (draft: unknown) => void) => Promise<unknown> = async (modifier) => {
        const produced = produce(data, modifier)
        await set(ref(database, query), produced)
        return produced;
    }
    const value: DataStates<unknown> = error.error ? {
        "status": "error",
        "reason": error.message,
        "retry": retryCallback
    } : (loading ? {
        "status": "loading"
    } : {
        "status": "done",
        "data": data,
        "modify": modifyCallback
    });
    //console.log("Called with Query: " + query)

    //console.log(data)
    return <DataContext.Provider value={value}>
        {children}
    </DataContext.Provider>
}


export type DataHandler = {
    state: DataState,
    handler: () => (JSX.Element | JSX.Element[])
}
export type DataStates<T> = {
    "status": "done",
    "data": T,
    "modify": (modifier: (draft: T) => void) => Promise<T>
} | {
    "status": "error",
    "reason": string,
    "retry": () => void
} | {
    "status": "loading",
}
export type DataState = DataStates<unknown>["status"]

function generateInsecureUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        // For the 'y' position, the value must be 8, 9, A, or B (binary 10xx)
        // to conform to the UUID standard.
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InnerDBContext: React.Context<(handler: DataHandler) => () => void> = createContext((_id) => () => { })

function InnerRoutedDBProvider<T>({
    children
}: {
    children: JSX.Element | JSX.Element[]
}) {
    const data = useDatabase<T>();
    const [handlers, setHandlers] = useState({} as Record<string, DataHandler>)
    const handlersRef: React.Ref<Record<string, DataHandler>> = useRef({});
    const matched = Object.keys(handlers).filter(x => handlers[x].state == data?.status)
    return <InnerDBContext.Provider value={(handler: DataHandler) => {
        const hashedid = getFunctionID(handler.handler);
        if (handlersRef.current != null && handlersRef.current[hashedid] != null) {
            console.log("SAME FOUND: " + hashedid)
            return () => {
                const cloned = { ...handlersRef.current }
                delete cloned[hashedid];
                handlersRef.current = cloned;
                setHandlers(cloned);
            }
        }
        setHandlers({
            ...handlersRef.current,
            [hashedid]: handler
        })
        handlersRef.current = {
            ...handlersRef.current,
            [hashedid]: handler
        }
        return () => {
            const cloned = { ...handlersRef.current }
            delete cloned[hashedid];
            handlersRef.current = cloned;
            setHandlers(cloned);
        }
    }}>
        {children}
        {matched.map(x => <Fragment key={generateInsecureUUID()}>{handlers[x].handler()}</Fragment>)}
    </InnerDBContext.Provider>
}

type AnyFunctionSafer = (...args: unknown[]) => unknown;

const getFunctionID = (func: AnyFunctionSafer) => MurmurHash3.hash32(func.toString(), 0).toString('hex');

export function DoneDataHandler<T>({ dataHandler }: {
    dataHandler: (
        data: T | null,
        modify: (modifier: (draft: T) => void) => Promise<T>
    ) => JSX.Element | JSX.Element[]
}): JSX.Element {
    const data: {
        "status": "done",
        "data": T | null,
        "modify": (modifier: (draft: T) => void) => Promise<T>
    } | null = useDatabase<T>() as {
        "status": "done",
        "data": T | null,
        "modify": (modifier: (draft: T) => void) => Promise<T>
    } | null;
    const ctx = useContext(InnerDBContext);
    useEffect(() => {
        return ctx({
            handler: () => dataHandler(data?.data ?? null, data?.modify ?? (() => { return new Promise(() => { }) })),
            state: "done"
        })
    }, [data?.data, data?.modify, dataHandler])
    return <></>
}


export function ErrorDataHandler({ dataHandler }: {
    dataHandler: (
        reason: string,
        retry: () => void
    ) => JSX.Element | JSX.Element[]
}): JSX.Element {
    const error: {
        "status": "error",
        "reason": string,
        "retry": () => void
    } = useDatabase<unknown>() as {
        "status": "error",
        "reason": string,
        "retry": () => void
    };
    const ctx = useContext(InnerDBContext);
    useEffect(() => {
        return ctx({
            handler: () => dataHandler(error.reason, error.retry),
            state: "error"
        } as DataHandler)
    }, [dataHandler, error.reason, error.retry])
    return <></>
}

export function LoadingDataHandler({ dataHandler }: {
    dataHandler: () => JSX.Element | JSX.Element[]
}): JSX.Element {
    const ctx = useContext(InnerDBContext);
    useEffect(() => {
        return ctx({
            handler: () => dataHandler(),
            state: "loading"
        } as DataHandler)
    }, [dataHandler])
    return <></>
}


export function RoutedDBProvider({
    query,
    children
}: {
    query: string,
    children: JSX.Element | JSX.Element[]
}) {
    return <DatabaseProvider query={query}>
        <InnerRoutedDBProvider>
            {children}
        </InnerRoutedDBProvider>
    </DatabaseProvider>
}

